import {
    ClockCircleOutlined,
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    MoreOutlined,
    SendOutlined
} from '@ant-design/icons'
import { useChannelStore } from '@entities/channel'
import type { Post } from '@entities/post'
import { AddPostModal, EditPostModal, PreviewPostModal, usePosts } from '@entities/post'
import { SchedulePostModal } from '@entities/post/ui/schedule-post-modal/schedule-post-modal'
import { EDisplayPosts } from '@pages/scheduled-posts-page/ui/scheduled-posts-page'
import { dateToString } from '@shared/lib/dateToString/dateToString'
import { CopyButton } from '@shared/ui/copy-button/copy-button'
import type { TablePaginationConfig, TableProps } from 'antd'
import { Button, Card, Dropdown, Flex, Input, Modal, Popconfirm, Space, Table, theme, Typography } from 'antd'
import type { ItemType } from 'antd/lib/menu/interface'
import { useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

const { Title, Link, Text } = Typography

export const DraftPostsPage = () => {
    const {
        token: { colorLink }
    } = theme.useToken()

    const [post, setPost] = useState<undefined | Post>()
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [editPostData, setEditPost] = useState<Post | null>(null)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
    const [isSharedLinkOpen, setIsSharedLinkOpen] = useState(false)

    const { currentChannel } = useChannelStore(useShallow(({ current }) => ({ currentChannel: current })))

    const {
        posts,
        pagination,
        isPostsLoading,
        sharedData,
        actions: {
            setSharedData,
            clearStateShare,
            setPagination,
            deletePost,
            createPost,
            publishPostNow,
            previewPost,
            createShareLink,
            deleteShareLink
        }
    } = usePosts({
        state: 'draft',
        display: EDisplayPosts.all,
        channelId: currentChannel?.id
    })

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setPagination({ page: pagination.current || 0, pageSize: pagination.pageSize || 0 })
    }

    const onOpenSharedLink = (post: Post) => {
        if (post.isShared) {
            createShareLink(post.id)
        } else {
            setSharedData({ id: post.id, isShared: post.isShared })
        }
        setIsSharedLinkOpen(true)
    }

    const onCloseSharedLink = () => {
        setIsSharedLinkOpen(false)
        clearStateShare()
    }

    const onCreateSharedLink = () => {
        createShareLink(sharedData.id)
    }

    const onCloseAccessSharedLink = () => {
        deleteShareLink()
    }

    const onScheduleModalOpen = (post: Post) => {
        setEditPost(post)
        setIsScheduleModalOpen(true)
    }

    const onScheduleModalClose = () => {
        setEditPost(null)
        setIsScheduleModalOpen(false)
    }

    const onEditModalOpen = (post: Post) => {
        setEditPost(post)
        setIsEditModalOpen(true)
    }

    const onEditModalClose = () => {
        setEditPost(null)
        setIsEditModalOpen(false)
    }
    const handleSelectEvent = (post: Post) => {
        setPost(post)
        setIsPreviewModalOpen(true)
    }
    const stopProp = (e: React.MouseEvent) => {
        e.stopPropagation()
    }
    const columns: TableProps<Post>['columns'] = [
        {
            title: 'Создатель',
            dataIndex: 'creator',
            key: 'creator',
            render: (_, record) => {
                return record.creator.username
            }
        },
        {
            title: 'Добавлен',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (_, record) => {
                return dateToString(record.createdAt)
            }
        },
        {
            title: 'Обновлен',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            render: (_, record) => {
                return dateToString(record.updatedAt)
            }
        },
        {
            title: 'Действия',
            key: 'action',
            render: (_, record) => (
                <Space onClick={stopProp} size="middle">
                    <Link onClick={() => onScheduleModalOpen(record)}>
                        <ClockCircleOutlined /> Запланировать
                    </Link>
                    <Link onClick={() => onEditModalOpen(record)}>
                        <EditOutlined /> Редактировать
                    </Link>
                    <Dropdown
                        trigger={['click']}
                        menu={{
                            items: [
                                {
                                    key: '0',
                                    label: (
                                        <Space style={{ color: colorLink }}>
                                            <EyeOutlined /> Превью поста
                                        </Space>
                                    ),
                                    onClick: () => handleSelectEvent(record)
                                },
                                {
                                    key: '1',
                                    label: (
                                        <Popconfirm
                                            title="Создать копию"
                                            description="Вы уверены что хотите создать копию поста?"
                                            onConfirm={() => createPost(record)}
                                            okText="Создать"
                                            cancelText="Отмена"
                                        >
                                            <Space style={{ color: colorLink, width: '100%' }}>
                                                <CopyOutlined /> Создать копию
                                            </Space>
                                        </Popconfirm>
                                    )
                                },
                                {
                                    key: '2',
                                    label: (
                                        <Space
                                            onClick={() => onOpenSharedLink(record)}
                                            style={{ color: colorLink, width: '100%' }}
                                        >
                                            <SendOutlined /> Доступ по ссылке
                                        </Space>
                                    )
                                },
                                {
                                    key: '3',
                                    label: (
                                        <Popconfirm
                                            title="Опубликовать сейчас"
                                            description="Вы уверены что хотите опубликовать пост?"
                                            onConfirm={() => publishPostNow(record.id)}
                                            okText="Опубликовать"
                                            cancelText="Отмена"
                                        >
                                            <Space style={{ color: colorLink, width: '100%' }}>
                                                <SendOutlined /> Опубликовать сейчас
                                            </Space>
                                        </Popconfirm>
                                    )
                                },
                                {
                                    key: '4',
                                    label: (
                                        <Space
                                            onClick={() => previewPost(record)}
                                            style={{ color: colorLink, width: '100%' }}
                                        >
                                            <EyeOutlined /> Предпросмотр в бот
                                        </Space>
                                    )
                                },
                                {
                                    key: '5',
                                    danger: true,
                                    label: (
                                        <Popconfirm
                                            title="Удалить пост"
                                            description="Вы уверены что хотите удалить пост?"
                                            onConfirm={() => deletePost(record.id)}
                                            okText="Удалить"
                                            cancelText="Отмена"
                                        >
                                            <Space style={{ width: '100%' }}>
                                                <DeleteOutlined /> Удалить
                                            </Space>
                                        </Popconfirm>
                                    )
                                }
                            ].filter(Boolean) as ItemType[]
                        }}
                    >
                        <MoreOutlined />
                    </Dropdown>
                </Space>
            )
        }
    ]

    return (
        <Card>
            <Flex justify="space-between" align="center" style={{ marginBottom: '16px' }}>
                <Title level={3}>Черновики</Title>
                <Button type="primary" onClick={() => setIsCreateModalOpen(true)}>
                    Создать пост
                </Button>
            </Flex>

            <Table<Post>
                columns={columns}
                dataSource={posts?.results}
                loading={isPostsLoading}
                pagination={{
                    current: pagination.page,
                    pageSize: pagination.pageSize,
                    total: posts?.count || 0,
                    showSizeChanger: true
                }}
                onChange={handleTableChange}
                onRow={(record) => ({
                    onClick: () => {
                        handleSelectEvent(record)
                    }
                })}
                scroll={{
                    y: 'calc(100vh - 441px)'
                }}
                rowKey="id"
            />

            {isCreateModalOpen && <AddPostModal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />}
            {editPostData && (
                <EditPostModal editPostData={editPostData} open={isEditModalOpen} onClose={onEditModalClose} />
            )}

            {editPostData && (
                <SchedulePostModal
                    open={isScheduleModalOpen}
                    editPostData={editPostData}
                    onClose={onScheduleModalClose}
                />
            )}

            <Modal
                open={isSharedLinkOpen}
                onCancel={onCloseSharedLink}
                onOk={sharedData.sharedId || sharedData.isShared ? onCloseAccessSharedLink : onCreateSharedLink}
                centered
                title="Доступ по ссылке"
                okText={sharedData.sharedId || sharedData.isShared ? 'Закрыть доступ' : 'Открыть доступ'}
                cancelText="Отменить"
            >
                {sharedData.sharedId ? (
                    <Space>
                        <Input
                            style={{ width: '300px' }}
                            value={`https://telegaplan.ru/posts/${sharedData.id}/shared/${sharedData.sharedId}`}
                        />
                        <CopyButton
                            textToCopy={`https://telegaplan.ru/posts/${sharedData.id}/shared/${sharedData.sharedId}`}
                        />
                    </Space>
                ) : (
                    <Text>По ссылке будет доступна возможность просматривать пост и делать его копию.</Text>
                )}
            </Modal>
            <PreviewPostModal
                onEditModalOpen={onEditModalOpen}
                post={post}
                open={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
            />
        </Card>
    )
}
